import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  message,
  Modal,
  Form,
  Input,
  Select,
  Row,
  Col,
  Typography,
  Tooltip,
  Popconfirm,
  Drawer,
  Transfer,
  Spin,
  Divider,
  Badge,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  KeyOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  ApiOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  getParkPassRoles,
  createParkPassRole,
  updateParkPassRole,
  deleteParkPassRole,
  updateRoleEndpoints,
  getAllEndpoints,
} from "../../api/parkpass/parkpassRoles";
import PermissionRender from "../../components/PermissionRender";

const { Title, Text } = Typography;
const { TextArea } = Input;

export default function CitizenRolesPage() {
  const [roles, setRoles] = useState([]);
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [endpointsLoading, setEndpointsLoading] = useState(false);

  // Role Modal
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  // Endpoints Drawer
  const [isEndpointsDrawerVisible, setIsEndpointsDrawerVisible] =
    useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedEndpointIds, setSelectedEndpointIds] = useState([]);
  const [endpointSearchText, setEndpointSearchText] = useState("");

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getParkPassRoles();
      setRoles(data);
    } catch (error) {
      message.error("Erreur lors du chargement des rôles");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEndpoints = useCallback(async () => {
    setEndpointsLoading(true);
    try {
      const data = await getAllEndpoints();
      setEndpoints(data.endpoints || []);
    } catch (error) {
      message.error("Erreur lors du chargement des endpoints");
      console.error(error);
    } finally {
      setEndpointsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
    fetchEndpoints();
  }, [fetchRoles, fetchEndpoints]);

  // Role Modal Handlers
  const showCreateModal = () => {
    setEditingRole(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const showEditModal = (role) => {
    setEditingRole(role);
    form.setFieldsValue({
      name: role.name,
      description: role.description,
    });
    setIsModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setEditingRole(null);
    form.resetFields();
  };

  const handleModalSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      if (editingRole) {
        await updateParkPassRole(editingRole.role_id, {
          name: values.name,
          description: values.description,
          endpoints: editingRole.endpoints || "",
        });
        message.success("Rôle mis à jour avec succès");
      } else {
        await createParkPassRole({
          name: values.name,
          description: values.description,
          endpoints: "",
        });
        message.success("Rôle créé avec succès");
      }

      handleModalCancel();
      fetchRoles();
    } catch (error) {
      message.error(error.message || "Erreur lors de l'opération");
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (roleId) => {
    try {
      await deleteParkPassRole(roleId);
      message.success("Rôle supprimé avec succès");
      fetchRoles();
    } catch (error) {
      message.error("Erreur lors de la suppression du rôle");
      console.error(error);
    }
  };

  // Endpoints Drawer Handlers
  const showEndpointsDrawer = (role) => {
    setSelectedRole(role);

    // Parse the endpoints string to get selected endpoint IDs
    if (role.endpoints === "full-access") {
      // For full-access, select all endpoints
      setSelectedEndpointIds(endpoints.map((e) => e.id));
    } else if (role.endpoints) {
      // Parse comma-separated endpoint IDs
      const ids = role.endpoints
        .split(",")
        .map((id) => parseInt(id.trim()))
        .filter((id) => !isNaN(id));
      setSelectedEndpointIds(ids);
    } else {
      setSelectedEndpointIds([]);
    }

    setIsEndpointsDrawerVisible(true);
  };

  const handleEndpointsDrawerClose = () => {
    setIsEndpointsDrawerVisible(false);
    setSelectedRole(null);
    setSelectedEndpointIds([]);
    setEndpointSearchText("");
  };

  const handleEndpointsChange = (nextTargetKeys) => {
    setSelectedEndpointIds(nextTargetKeys);
  };

  const handleSaveEndpoints = async () => {
    if (!selectedRole) return;

    try {
      setSubmitting(true);

      // Convert selected endpoint IDs to comma-separated string
      const endpointsString = selectedEndpointIds.join(",");

      await updateRoleEndpoints(selectedRole.name, endpointsString);
      message.success("Endpoints mis à jour avec succès");

      handleEndpointsDrawerClose();
      fetchRoles();
    } catch (error) {
      message.error("Erreur lors de la mise à jour des endpoints");
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSetFullAccess = async () => {
    if (!selectedRole) return;

    try {
      setSubmitting(true);
      await updateRoleEndpoints(selectedRole.name, "full-access");
      message.success("Accès complet activé avec succès");

      handleEndpointsDrawerClose();
      fetchRoles();
    } catch (error) {
      message.error("Erreur lors de la mise à jour des endpoints");
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  // Filter endpoints by search
  const filterOption = (inputValue, option) => {
    return (
      option.name?.toLowerCase().includes(inputValue.toLowerCase()) ||
      option.path?.toLowerCase().includes(inputValue.toLowerCase()) ||
      option.methods
        ?.join(", ")
        .toLowerCase()
        .includes(inputValue.toLowerCase())
    );
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "role_id",
      key: "role_id",
      width: 80,
    },
    {
      title: "Nom",
      dataIndex: "name",
      key: "name",
      render: (text) => (
        <Space>
          <SafetyCertificateOutlined style={{ color: "#1890ff" }} />
          <Text strong>{text}</Text>
        </Space>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
    },
    {
      title: "Endpoints",
      dataIndex: "endpoints",
      key: "endpoints",
      width: 200,
      render: (text) => {
        if (text === "full-access") {
          return <Tag color="green">Accès complet</Tag>;
        } else if (!text) {
          return <Tag color="default">Aucun endpoint</Tag>;
        } else {
          const count = text.split(",").filter((e) => e.trim()).length;
          return <Tag color="blue">{count} endpoint(s)</Tag>;
        }
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 200,
      render: (_, record) => (
        <Space>
          <PermissionRender requiredPermission={["citizen_roles", "update"]}>
            <Tooltip title="Modifier">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => showEditModal(record)}
              />
            </Tooltip>
          </PermissionRender>
          <PermissionRender requiredPermission={["citizen_roles", "manage"]}>
            <Tooltip title="Gérer les endpoints">
              <Button
                type="text"
                icon={<KeyOutlined style={{ color: "#722ed1" }} />}
                onClick={() => showEndpointsDrawer(record)}
              />
            </Tooltip>
            <Popconfirm
              title="Supprimer ce rôle?"
              description="Cette action est irréversible"
              onConfirm={() => handleDelete(record.role_id)}
              okText="Oui"
              cancelText="Non"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="Supprimer">
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  danger
                  disabled={record.name === "superadmin"}
                />
              </Tooltip>
            </Popconfirm>
          </PermissionRender>
        </Space>
      ),
    },
  ];

  // Transfer data source
  const transferDataSource = endpoints.map((endpoint) => ({
    key: endpoint.id,
    title: endpoint.name,
    description: `${endpoint.methods?.join(", ")} ${endpoint.path}`,
    ...endpoint,
  }));

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 16 }}
        >
          <Col>
            <Title level={4} style={{ margin: 0 }}>
              <SafetyCertificateOutlined style={{ marginRight: 8 }} />
              Gestion des Rôles
            </Title>
          </Col>
          <Col>
            <Space>
              <Button
                icon={<ReloadOutlined />}
                onClick={fetchRoles}
                loading={loading}
              >
                Actualiser
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={showCreateModal}
              >
                Nouveau Rôle
              </Button>
            </Space>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={roles}
          loading={loading}
          rowKey="role_id"
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} sur ${total} rôles`,
          }}
        />
      </Card>

      {/* Role Modal */}
      <Modal
        title={editingRole ? "Modifier le rôle" : "Nouveau rôle"}
        open={isModalVisible}
        onCancel={handleModalCancel}
        onOk={handleModalSubmit}
        confirmLoading={submitting}
        okText={editingRole ? "Mettre à jour" : "Créer"}
        cancelText="Annuler"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label="Nom du rôle"
            rules={[
              { required: true, message: "Veuillez saisir le nom du rôle" },
              {
                pattern: /^[a-z_]+$/,
                message:
                  "Le nom doit contenir uniquement des lettres minuscules et underscores",
              },
            ]}
          >
            <Input
              placeholder="ex: admin, moderator, viewer"
              disabled={editingRole?.name === "superadmin"}
            />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
            rules={[
              { required: true, message: "Veuillez saisir une description" },
            ]}
          >
            <TextArea rows={3} placeholder="Description du rôle..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Endpoints Drawer */}
      <Drawer
        title={
          <Space>
            <ApiOutlined />
            <span>Endpoints pour: {selectedRole?.name}</span>
          </Space>
        }
        placement="right"
        width={800}
        onClose={handleEndpointsDrawerClose}
        open={isEndpointsDrawerVisible}
        extra={
          <Space>
            <Button onClick={handleSetFullAccess} loading={submitting}>
              Accès complet
            </Button>
            <Button
              type="primary"
              onClick={handleSaveEndpoints}
              loading={submitting}
            >
              Enregistrer
            </Button>
          </Space>
        }
      >
        {endpointsLoading ? (
          <div style={{ textAlign: "center", padding: 50 }}>
            <Spin size="large" />
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary">
                Sélectionnez les endpoints auxquels ce rôle aura accès.
                {selectedRole?.endpoints === "full-access" && (
                  <Tag color="green" style={{ marginLeft: 8 }}>
                    Actuellement: Accès complet
                  </Tag>
                )}
              </Text>
            </div>

            <Input
              placeholder="Rechercher un endpoint..."
              prefix={<SearchOutlined />}
              value={endpointSearchText}
              onChange={(e) => setEndpointSearchText(e.target.value)}
              style={{ marginBottom: 16 }}
            />

            <Divider orientation="left">Endpoints disponibles</Divider>

            <Transfer
              dataSource={transferDataSource}
              titles={["Disponibles", "Attribués"]}
              targetKeys={selectedEndpointIds}
              onChange={handleEndpointsChange}
              filterOption={filterOption}
              showSearch
              listStyle={{
                width: 350,
                height: 450,
              }}
              render={(item) => (
                <div>
                  <div>
                    <Text strong>{item.name}</Text>
                  </div>
                  <div>
                    <Space size="small">
                      {item.methods?.map((method) => (
                        <Tag
                          key={method}
                          color={
                            method === "GET"
                              ? "blue"
                              : method === "POST"
                              ? "green"
                              : method === "PUT"
                              ? "orange"
                              : method === "DELETE"
                              ? "red"
                              : "default"
                          }
                          style={{ fontSize: 10 }}
                        >
                          {method}
                        </Tag>
                      ))}
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {item.path}
                      </Text>
                    </Space>
                  </div>
                </div>
              )}
            />

            <Divider />

            <Row gutter={16}>
              <Col span={12}>
                <Badge
                  status="processing"
                  text={`${transferDataSource.length} endpoints disponibles`}
                />
              </Col>
              <Col span={12}>
                <Badge
                  status="success"
                  text={`${selectedEndpointIds.length} endpoints attribués`}
                />
              </Col>
            </Row>
          </>
        )}
      </Drawer>
    </div>
  );
}
